import { Spacing } from '@persona-id/design-tokens';
import { useContext } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import styled, { ThemeContext } from 'styled-components';

import IconCamera from 'images/icons/inquiry/camera.svg';
import ImageIdBack from 'images/pictographs/id-back.svg';
import ImageIdFront from 'images/pictographs/id-front.svg';
import ImagePassportPortrait from 'images/pictographs/passport-portrait.svg';
import ImagePassportSignature from 'images/pictographs/passport-signature.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import VerificationHero from 'inquiry/components/VerificationHero';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { tForGovernmentId } from 'lib/governmentId';
import { PictographPosition } from 'lib/interfaces/ThemeSerialized';
import { GovernmentIdCaptureOption, GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { assertNever } from 'lib/utils/types';
import ButtonInputCapture from 'verify/components/ButtonInputCapture';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import { DisclaimerText } from 'verify/components/Disclaimer';
import { FullScreenLoadingSpinner } from 'verify/components/LoadingSpinner';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedTitle from 'verify/components/ThemedTitle';
import PromptOptions from 'verify/lib/interfaces/PromptOptions';
import { MIN_HEIGHTS } from 'verify/styles/variables';

export const ACCEPTED_IMAGE_FORMATS = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[4]};
`;

const Title = ({
  countryCode,
  governmentIdClass,
  governmentIdSide,
}: {
  countryCode: CountryCode;
  governmentIdClass: GovernmentIdClass;
  governmentIdSide: GovernmentIdSide;
}) => {
  const [t] = useTranslation();

  const title = tForGovernmentId(
    t,
    ['government-id.prompt.title', 'government-id.title'],
    countryCode,
    governmentIdClass,
    governmentIdSide,
  );

  return <ThemedTitle>{title}</ThemedTitle>;
};

const Prompt = ({
  governmentIdClass,
  governmentIdSide,
}: {
  governmentIdClass: GovernmentIdClass;
  governmentIdSide: GovernmentIdSide;
}) => {
  const [t] = useTranslation();
  const prompt = tForGovernmentId(
    t,
    ['government-id.prompt.prompt'],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    null,
    governmentIdClass,
    governmentIdSide,
  );
  return (
    <ThemedParagraph>
      <Markdown>{prompt}</Markdown>
    </ThemedParagraph>
  );
};

const CaptureOptionsContainer = styled.div`
  width: 100%;
`;

const OptionButton = styled(Button)`
  margin-bottom: ${Spacing[4]};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const ImgCamera = styled(IconCamera)`
  vertical-align: bottom;

  path[stroke] {
    stroke: ${(props) => props.theme.colorFontButtonPrimary};
  }
`;

export interface VerifyGovernmentIdPromptViewProps {
  currentSide: GovernmentIdSide;
  handleDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => void;
  handleSelectCamera: () => void;
  handleInputCapture: (file: Blob) => void;
  handleDeviceHandoff: () => void;
  isProcessing: PromptOptions;
  governmentIdClass: GovernmentIdClass;
  documentPromptCaptureButton: string | undefined;
  selectedCountryCode: CountryCode;
  enabledCaptureOptions: GovernmentIdCaptureOption[];
}

function CaptureOptions(props: VerifyGovernmentIdPromptViewProps) {
  return (
    <CaptureOptionsContainer id='government-id-prompt__view'>
      {props.enabledCaptureOptions.map((option, index) => (
        <OptionBlock key={option} isPrimary={index === 0} option={option} {...props} />
      ))}
    </CaptureOptionsContainer>
  );
}

interface OptionBlockProps {
  currentSide: GovernmentIdSide;
  handleDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => void;
  handleSelectCamera: () => void;
  handleInputCapture: (file: Blob) => void;
  handleDeviceHandoff: () => void;
  isProcessing: PromptOptions;
  documentPromptCaptureButton: string | undefined;
  isPrimary: boolean;
  option: GovernmentIdCaptureOption;
}

function OptionBlock(props: OptionBlockProps) {
  const {
    handleSelectCamera,
    handleInputCapture,
    handleDeviceHandoff,
    isProcessing,
    documentPromptCaptureButton,
    option,
  } = props;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: props.handleDrop,
    multiple: false,
    accept: ACCEPTED_IMAGE_FORMATS,
  });

  const [t] = useTranslation();

  const buttonDisabled = isProcessing != null;

  switch (option) {
    case GovernmentIdCaptureOption.WebCamera:
      return (
        <OptionButton
          id='government-id-prompt__button--web-camera'
          buttonType={ButtonType.Primary}
          disabled={buttonDisabled}
          onClick={handleSelectCamera}
          isPending={isProcessing === PromptOptions.SelectCamera}
          // note that documentPromptCaptureButton can be ''
          aria-label={documentPromptCaptureButton || t('government-id.prompt.btn-take-photo-label')}
        >
          <CaptureButtonContent>{documentPromptCaptureButton}</CaptureButtonContent>
        </OptionButton>
      );

    case GovernmentIdCaptureOption.MobileCamera:
      return (
        <ButtonInputCapture
          buttonType={ButtonType.Primary}
          disabled={buttonDisabled}
          id='government-id-prompt__button--mobile-camera'
          handleSelectImage={handleInputCapture}
          isPending={isProcessing === PromptOptions.UploadImage}
          // note that documentPromptCaptureButton can be ''
          aria-label={documentPromptCaptureButton || t('government-id.prompt.btn-take-photo-label')}
        >
          <CaptureButtonContent>{documentPromptCaptureButton}</CaptureButtonContent>
        </ButtonInputCapture>
      );

    case GovernmentIdCaptureOption.Upload:
      return (
        <OptionButton
          buttonType={props.isPrimary ? ButtonType.Primary : ButtonType.Secondary}
          disabled={buttonDisabled}
          {...getRootProps()}
        >
          <input {...getInputProps()} data-test='file-upload' data-cy='file-upload' />
          {t('government-id.prompt.upload')}
        </OptionButton>
      );

    case GovernmentIdCaptureOption.DeviceHandoff:
      return (
        <OptionButton
          id='government-id-prompt__button--device-handoff'
          data-test='government-id-prompt__button--device-handoff'
          buttonType={props.isPrimary ? ButtonType.Primary : ButtonType.Secondary}
          disabled={buttonDisabled}
          onClick={handleDeviceHandoff}
          isPending={isProcessing === PromptOptions.DeviceHandoff}
        >
          {t('government-id.prompt.btn-send-to-device')}
        </OptionButton>
      );

    default:
      assertNever(option);
  }
}

interface CaptureButtonContentProps {
  children: string | undefined;
}

const CaptureButtonContent = ({ children }: CaptureButtonContentProps) => {
  if (children) {
    return <>{children}</>;
  } else {
    return <ImgCamera data-test='government-id-prompt__image--camera' />;
  }
};

const StyledCustomizablePictograph = styled(CustomizablePictograph)`
  width: 100%;
`;

const Pictograph = ({
  currentSide,
  governmentIdClass,
}: {
  currentSide: GovernmentIdSide;
  governmentIdClass: GovernmentIdClass;
}) => {
  if (governmentIdClass === 'pp') {
    switch (currentSide) {
      case GovernmentIdSide.PassportSignature:
        return (
          <StyledCustomizablePictograph
            url='passportSignaturePictographUrl'
            height='passportSignaturePictographHeight'
            aria-label='passport signature'
            vectorEffect='initial'
          >
            <VerificationHero>
              <ImagePassportSignature height={64} width='100%' />
            </VerificationHero>
          </StyledCustomizablePictograph>
        );
      default:
        return (
          <StyledCustomizablePictograph
            url='passportFrontPictographUrl'
            height='passportFrontPictographHeight'
            aria-label='passport front'
            vectorEffect='initial'
          >
            <VerificationHero>
              <ImagePassportPortrait height={80} width='100%' />
            </VerificationHero>
          </StyledCustomizablePictograph>
        );
    }
  }

  switch (currentSide) {
    case GovernmentIdSide.Back:
    case GovernmentIdSide.BarcodePdf417:
      return (
        <StyledCustomizablePictograph
          url='idBackPictographUrl'
          height='idBackPictographHeight'
          aria-label='id back'
          vectorEffect='initial'
        >
          <VerificationHero>
            <ImageIdBack height={64} width='100%' />
          </VerificationHero>
        </StyledCustomizablePictograph>
      );

    case GovernmentIdSide.Front:
      return (
        <StyledCustomizablePictograph
          url='idFrontPictographUrl'
          height='idFrontPictographHeight'
          aria-label='id front'
          vectorEffect='initial'
        >
          <VerificationHero>
            <ImageIdFront height={64} width='100%' />
          </VerificationHero>
        </StyledCustomizablePictograph>
      );

    case GovernmentIdSide.FrontOrBack:
      return (
        <StyledCustomizablePictograph
          url='idFrontPictographUrl'
          height='idFrontPictographHeight'
          aria-label='id front'
          vectorEffect='initial'
        >
          <VerificationHero>
            <ImageIdFront height={48} width='100%' />
            <ImageIdBack height={48} width='100%' />
          </VerificationHero>
        </StyledCustomizablePictograph>
      );

    case GovernmentIdSide.PassportSignature:
      return (
        <StyledCustomizablePictograph
          url='passportSignaturePictographUrl'
          height='passportSignaturePictographHeight'
          aria-label='passport signature'
          vectorEffect='initial'
        >
          <VerificationHero>
            <ImagePassportSignature height={64} width='100%' />
          </VerificationHero>
        </StyledCustomizablePictograph>
      );

    default:
      assertNever(currentSide);
  }
};

const VerifyGovernmentIdPromptView = (props: VerifyGovernmentIdPromptViewProps) => {
  const { selectedCountryCode, currentSide, governmentIdClass, enabledCaptureOptions } = props;
  const { governmentIdPictographPosition } = useContext(ThemeContext);

  if (enabledCaptureOptions.length === 0) {
    return <FullScreenLoadingSpinner />;
  }

  // CenteredPictograph delegates a11y handling to Pictograph.
  const pictographNode = (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <CenteredPictograph height={null} width={null} role={null} aria-label={null}>
      <Pictograph currentSide={currentSide} governmentIdClass={governmentIdClass} />
    </CenteredPictograph>
  );

  const renderDisclaimer = selectedCountryCode === 'US' && currentSide === GovernmentIdSide.Back;

  return (
    <div>
      {governmentIdPictographPosition === PictographPosition.Start && pictographNode}

      <Header>
        <Title
          countryCode={selectedCountryCode}
          governmentIdClass={governmentIdClass}
          governmentIdSide={currentSide}
        />
        <Prompt governmentIdClass={governmentIdClass} governmentIdSide={currentSide} />
      </Header>

      {governmentIdPictographPosition === PictographPosition.Center && pictographNode}

      {renderDisclaimer && <DisclaimerText agreeToPolicyType='barcode' />}

      <PositionableButtonContainer>
        <CaptureOptions {...props} />
      </PositionableButtonContainer>
    </div>
  );
};

export default VerifyGovernmentIdPromptView;
